import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import HealthcareCapitalHeroVideo from "../components/marketing/HealthcareCapitalHeroVideo";
import MarketingFooter from "../components/marketing/MarketingFooter";
import MarketingSiteHeader from "../components/marketing/MarketingSiteHeader";
import {
  HERO,
  IMPACT_CLOSING,
  IMPACT_PILLARS,
  MISSION,
  PLATFORM_INSIGHTS,
  PLATFORM_PROGRAMS,
  STAY_CONNECTED,
} from "../data/platformPresentation";
import { fetchMarketingHome } from "../services/marketingService";

function formatCount(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return new Intl.NumberFormat("en").format(v);
}

function formatFunds(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  if (v >= 1_000_000) return `৳${(v / 1_000_000).toFixed(1)}M`;
  return new Intl.NumberFormat("en-BD", {
    style: "currency",
    currency: "BDT",
    maximumFractionDigits: 0,
  }).format(v);
}

function formatPillarStat(pillar, metrics) {
  const key = pillar.statKey;
  const val = metrics[key];
  if (val == null || val === "") return null;
  if (pillar.statFormat === "funds") return formatFunds(val);
  if (pillar.statFormat === "percent") return `${val}%`;
  return formatCount(val);
}

function renderBoldText(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function PillarIcon({ type }) {
  const icons = {
    projects: (
      <path d="M4 20V8l8-4 8 4v12M9 20v-6h6v6" stroke="currentColor" strokeWidth="1.5" fill="none" />
    ),
    wallet: (
      <path
        d="M4 8h16v12H4V8zm0-2h16a2 2 0 012 2v1H2V8a2 2 0 012-2zm14 6h2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
    ),
    certificates: (
      <path d="M7 4h10v16l-5-3-5 3V4z" stroke="currentColor" strokeWidth="1.5" fill="none" />
    ),
    governance: (
      <path d="M12 3l8 4v6c0 5-3.5 8-8 10C7.5 21 4 18 4 13V7l8-4z" stroke="currentColor" strokeWidth="1.5" fill="none" />
    ),
  };
  return (
    <svg className="nh-rh__pillar-icon" viewBox="0 0 24 24" aria-hidden>
      {icons[type] || icons.projects}
    </svg>
  );
}

function statusBadgeClass(label) {
  const s = String(label || "").toLowerCase();
  if (s.includes("fundraising")) return "nh-rh__badge nh-rh__badge--fundraising";
  if (s.includes("funded")) return "nh-rh__badge nh-rh__badge--funded";
  return "nh-rh__badge nh-rh__badge--active";
}

function FeaturedProjectCard({ project, large }) {
  const pct = Math.min(100, Math.max(0, Number(project.funding_progress_pct) || 0));
  return (
    <article className={`nh-rh__project-card${large ? " nh-rh__project-card--large" : ""}`}>
      <div className="nh-rh__project-card-head">
        <div>
          {project.code ? <span className="nh-rh__project-code">{project.code}</span> : null}
          <h3>{project.name}</h3>
        </div>
        <span className={statusBadgeClass(project.status_label)}>{project.status_label}</span>
      </div>
      <p className="nh-rh__project-desc">{project.description || "Healthcare investment opportunity."}</p>
      {project.expected_return_pct ? (
        <div className="nh-rh__project-meta">
          <span className="nh-rh__project-return">~{project.expected_return_pct}% indicative return</span>
        </div>
      ) : null}
      <div className="nh-rh__progress">
        <div className="nh-rh__progress-meta">
          <span>Funding</span>
          <span>{pct.toFixed(0)}%</span>
        </div>
        <div className="nh-rh__progress-track" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className="nh-rh__progress-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </article>
  );
}

function MissionParagraph({ text, highlights, onSection }) {
  let nodes = [text];
  highlights.forEach(({ text: phrase, section }) => {
    const next = [];
    nodes.forEach((node) => {
      if (typeof node !== "string") {
        next.push(node);
        return;
      }
      const idx = node.indexOf(phrase);
      if (idx === -1) {
        next.push(node);
        return;
      }
      const before = node.slice(0, idx);
      const after = node.slice(idx + phrase.length);
      if (before) next.push(before);
      next.push(
        <button key={`${phrase}-${section}`} type="button" className="nh-rh__text-link" onClick={() => onSection(section)}>
          {phrase}
        </button>
      );
      if (after) next.push(after);
    });
    nodes = next;
  });
  return <p>{nodes}</p>;
}

export default function MarketingHomePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const hash = (location.hash || window.location.hash || "").replace("#", "");
    if (!hash) return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [location.hash, location.pathname]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: payload } = await fetchMarketingHome();
        if (!cancelled) setData(payload);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const metrics = data?.metrics ?? {};
  const projects = (data?.featured_projects ?? []).slice(0, 6);
  const spotlight = projects[0];
  const gridProjects = projects.slice(1, 5);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const featuredProgram = PLATFORM_PROGRAMS.find((p) => p.featured);
  const otherPrograms = PLATFORM_PROGRAMS.filter((p) => !p.featured);

  return (
    <div className="nh-rh">
      <MarketingSiteHeader />

      {/* Hero — Rock Health Capital style full-bleed video */}
      <section className="nh-rh__hero nh-rh__hero--video nh-rh__hero--capital">
        <HealthcareCapitalHeroVideo className="nh-rh__hero-video" />
        <div className="nh-rh__hero-capital-content">
          <h1 className="nh-rh__hero-capital-title">{HERO.title}</h1>
          <p className="nh-rh__hero-capital-lead">{HERO.lead}</p>
        </div>
        <div className="nh-rh__hero-scroll-line" aria-hidden />
      </section>

      {/* Mission */}
      <section id="mission" className="nh-rh__mission">
        <div className="nh-rh__container nh-rh__mission-inner">
          <h2 className="nh-rh__serif-title">{MISSION.title}</h2>
          {MISSION.paragraphs.map((para, i) => (
            <MissionParagraph
              key={i}
              text={para}
              highlights={i === 1 ? MISSION.highlightPhrases : []}
              onSection={scrollTo}
            />
          ))}
        </div>
      </section>

      {/* How we make an impact */}
      <section id="platform" className="nh-rh__impact-section">
        <div className="nh-rh__container">
          <h2 className="nh-rh__impact-heading">How we make an impact</h2>
          <p className="nh-rh__impact-lead">We advance healthcare investment through four key priorities:</p>
          <ul className="nh-rh__pillars">
            {IMPACT_PILLARS.map((pillar) => {
              const stat = formatPillarStat(pillar, metrics);
              return (
                <li key={pillar.id} className="nh-rh__pillar">
                  <div className="nh-rh__pillar-icon-wrap">
                    <PillarIcon type={pillar.icon} />
                  </div>
                  <div className="nh-rh__pillar-body">
                    <h3>{pillar.title}</h3>
                    <p>{renderBoldText(pillar.text)}</p>
                    {stat != null ? (
                      <p className="nh-rh__pillar-stat">
                        <strong>{stat}</strong> {pillar.statLabel}
                      </p>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="nh-rh__impact-close">{IMPACT_CLOSING}</p>
        </div>
      </section>

      {/* Featured programs */}
      <section className="nh-rh__programs">
        <div className="nh-rh__container">
          {featuredProgram ? (
            <article className="nh-rh__program nh-rh__program--featured">
              {featuredProgram.eyebrow ? <p className="nh-rh__program-eyebrow">{featuredProgram.eyebrow}</p> : null}
              <h2>{featuredProgram.title}</h2>
              <p>{featuredProgram.description}</p>
              {featuredProgram.to ? (
                <Link to={featuredProgram.to} className="nh-rh__program-cta">
                  {featuredProgram.cta}
                </Link>
              ) : (
                <button type="button" className="nh-rh__program-cta" onClick={() => scrollTo(featuredProgram.section)}>
                  {featuredProgram.cta}
                </button>
              )}
            </article>
          ) : null}
          <div className="nh-rh__program-grid">
            {otherPrograms.map((prog) => (
              <article key={prog.id} className="nh-rh__program nh-rh__program--card">
                <h3>{prog.title}</h3>
                <p>{prog.description}</p>
                {prog.to ? (
                  <Link to={prog.to} className="nh-rh__program-cta nh-rh__program-cta--sm">
                    {prog.cta}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="nh-rh__program-cta nh-rh__program-cta--sm"
                    onClick={() => scrollTo(prog.section)}
                  >
                    {prog.cta}
                  </button>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Opportunities */}
      <section id="opportunities" className="nh-rh__opportunities">
        <div className="nh-rh__container">
          <h2 className="nh-rh__section-title">Healthcare investment opportunities</h2>
          <p className="nh-rh__section-lead">Verified projects on the platform — open an account for full detail and allocation.</p>
          {loading ? <p className="nh-rh__muted">Loading opportunities…</p> : null}
          {!loading && projects.length === 0 ? (
            <p className="nh-rh__muted">Published projects will appear here. Register to access the live catalog.</p>
          ) : null}
          {spotlight ? (
            <div className="nh-rh__spotlight">
              <FeaturedProjectCard project={spotlight} large />
            </div>
          ) : null}
          {gridProjects.length > 0 ? (
            <div className="nh-rh__project-grid">
              {gridProjects.map((p) => (
                <FeaturedProjectCard key={p.id} project={p} />
              ))}
            </div>
          ) : null}
          <p className="nh-rh__section-cta">
            <Link to="/signup" className="nh-rh__btn nh-rh__btn--primary">
              Open account to invest
            </Link>
          </p>
        </div>
      </section>

      {/* Insights */}
      <section id="insights" className="nh-rh__insights">
        <div className="nh-rh__container">
          <h2 className="nh-rh__section-title">Insights</h2>
          <p className="nh-rh__section-lead">What matters now for healthcare investors on Need Healthcare.</p>
          <div className="nh-rh__insight-grid">
            {PLATFORM_INSIGHTS.map((item) => (
              <article key={item.title} className="nh-rh__insight-card">
                <span className="nh-rh__insight-category">{item.category}</span>
                <h3>
                  <button type="button" className="nh-rh__insight-link" onClick={() => scrollTo(item.section)}>
                    {item.title}
                  </button>
                </h3>
                <p>{item.excerpt}</p>
                <button type="button" className="nh-rh__read-link" onClick={() => scrollTo(item.section)}>
                  Read
                </button>
              </article>
            ))}
          </div>
          <p className="nh-rh__insights-more">
            <button type="button" className="nh-rh__text-link" onClick={() => scrollTo("platform")}>
              Explore the platform
            </button>
          </p>
        </div>
      </section>

      {/* Global impact — hidden for now (stats not ready to show publicly) */}

      {/* Stay connected */}
      <section className="nh-rh__subscribe">
        <div className="nh-rh__container nh-rh__subscribe-inner">
          <h2 className="nh-rh__subscribe-title">{STAY_CONNECTED.title}</h2>
          <p>{STAY_CONNECTED.lead}</p>
          <div className="nh-rh__subscribe-actions">
            <Link to={STAY_CONNECTED.to} className="nh-rh__btn nh-rh__btn--primary">
              {STAY_CONNECTED.cta}
            </Link>
            <Link to={STAY_CONNECTED.secondaryTo} className="nh-rh__btn nh-rh__btn--ghost">
              {STAY_CONNECTED.secondaryCta}
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter onSectionScroll={scrollTo} />
    </div>
  );
}
